import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Trophy, Medal, Crown, TrendingUp, Award, Star, RefreshCw, AlertCircle } from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { formatNumber } from '@/lib/utils';
import { leaderboardApi } from '@/lib/api';

interface LeaderboardPlayer {
    rank: number;
    userId: string;
    name: string;
    username: string;
    avatar: string | null;
    level: number;
    totalWins: number;
    totalGames: number;
    winRate: number;
    earnings?: number;
}

// Animation variants
const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
        opacity: 1,
        transition: { staggerChildren: 0.1 },
    },
};

const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
};

export default function Leaderboard() {
    const [topPlayers, setTopPlayers] = useState<LeaderboardPlayer[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [isRefreshing, setIsRefreshing] = useState(false);

    const fetchLeaderboard = useCallback(async () => {
        try {
            setError(null);
            const response = await leaderboardApi.getLeaderboard({ limit: 10 });
            const data = response.data;

            if (data.success && data.data?.leaderboard) {
                const mappedPlayers: LeaderboardPlayer[] = data.data.leaderboard.map((player: any) => ({
                    rank: player.rank,
                    userId: player.userId,
                    name: player.name || 'Anonymous',
                    username: player.username ? `@${player.username}` : '@user',
                    avatar: player.avatar,
                    level: player.level || 1,
                    totalWins: player.totalWins || 0,
                    totalGames: player.totalGames || 0,
                    winRate: player.winRate || 0,
                    earnings: player.earnings || 0,
                }));
                setTopPlayers(mappedPlayers);
            }
        } catch (err: any) {
            console.error('Failed to fetch leaderboard:', err);
            setError(err.message || 'Failed to load leaderboard');
        } finally {
            setLoading(false);
            setIsRefreshing(false);
        }
    }, []);

    useEffect(() => {
        fetchLeaderboard();
    }, [fetchLeaderboard]);

    const handleRefresh = () => {
        setIsRefreshing(true);
        fetchLeaderboard();
    };

    const getRankBadge = (rank: number) => {
        if (rank === 1) return <Crown className="w-5 h-5 text-yellow-400" />;
        if (rank === 2) return <Medal className="w-5 h-5 text-slate-300" />;
        if (rank === 3) return <Medal className="w-5 h-5 text-orange-400" />;
        return <Award className="w-5 h-5 text-slate-500" />;
    };

    const getRankBgColor = (rank: number) => {
        if (rank === 1) return 'bg-gradient-to-r from-yellow-500/20 to-amber-500/20 border-yellow-500/30';
        if (rank === 2) return 'bg-gradient-to-r from-slate-400/20 to-slate-500/20 border-slate-400/30';
        if (rank === 3) return 'bg-gradient-to-r from-orange-500/20 to-orange-600/20 border-orange-500/30';
        return 'bg-slate-800/50 border-slate-700/50';
    };

    const getInitials = (name: string) => {
        return name.split(' ').map(n => n[0]).join('').toUpperCase();
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <RefreshCw className="w-8 h-8 animate-spin text-violet-400" />
            </div>
        );
    }

    return (
        <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="space-y-6"
        >
            {/* Header */}
            <motion.div variants={itemVariants}>
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-2xl font-bold flex items-center gap-3">
                            <Trophy className="w-7 h-7 text-yellow-400" />
                            Leaderboard
                        </h1>
                        <p className="text-slate-400 mt-1">Top performing players on the platform</p>
                    </div>
                    <Button variant="outline" size="sm" onClick={handleRefresh} disabled={isRefreshing}>
                        <RefreshCw className={`w-4 h-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
                        Refresh
                    </Button>
                </div>
            </motion.div>

            {/* Error Message */}
            {error && (
                <Card variant="glass" className="p-4 border-red-500/50">
                    <div className="flex items-center gap-3 text-red-400">
                        <AlertCircle className="w-5 h-5" />
                        <span>{error}</span>
                    </div>
                </Card>
            )}

            {topPlayers.length === 0 ? (
                <Card variant="glass" className="p-8 text-center">
                    <p className="text-slate-400">No players on the leaderboard yet</p>
                </Card>
            ) : (
                <>
                    {/* Top 3 Podium */}
                    <motion.div variants={itemVariants} className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {topPlayers.slice(0, 3).map((player) => (
                            <Card
                                key={player.rank}
                                variant="glass"
                                className={`p-6 text-center ${getRankBgColor(player.rank)} border`}
                            >
                                <div className="flex flex-col items-center">
                                    <div className="mb-3">
                                        {getRankBadge(player.rank)}
                                    </div>
                                    <div className="w-16 h-16 rounded-full bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center text-white font-bold text-xl mb-3">
                                        {getInitials(player.name)}
                                    </div>
                                    <h3 className="font-bold text-lg">{player.name}</h3>
                                    <p className="text-sm text-slate-400 mb-3">{player.username}</p>
                                    <div className="flex items-center gap-2 mb-2">
                                        <Star className="w-4 h-4 text-yellow-400" />
                                        <span className="text-sm font-medium">Level {player.level}</span>
                                    </div>
                                    <div className="grid grid-cols-2 gap-4 w-full mt-4 pt-4 border-t border-slate-700">
                                        <div>
                                            <p className="text-xs text-slate-500">Wins</p>
                                            <p className="font-bold">{formatNumber(player.totalWins)}</p>
                                        </div>
                                        <div>
                                            <p className="text-xs text-slate-500">Games</p>
                                            <p className="font-bold">{formatNumber(player.totalGames)}</p>
                                        </div>
                                    </div>
                                    <div className="mt-2 w-full">
                                        <p className="text-xs text-slate-500">Win Rate</p>
                                        <div className="flex items-center gap-2 justify-center mt-1">
                                            <div className="flex-1 h-2 bg-slate-700 rounded-full overflow-hidden">
                                                <div
                                                    className="h-full bg-gradient-to-r from-emerald-500 to-green-400"
                                                    style={{ width: `${player.winRate}%` }}
                                                />
                                            </div>
                                            <span className="text-sm font-medium">{player.winRate.toFixed(1)}%</span>
                                        </div>
                                    </div>
                                </div>
                            </Card>
                        ))}
                    </motion.div>

                    {/* Full Leaderboard */}
                    <motion.div variants={itemVariants}>
                        <Card variant="glass" className="p-6">
                            <h3 className="text-lg font-semibold mb-4">Top Players</h3>
                            <div className="space-y-2">
                                {topPlayers.map((player) => (
                                    <div
                                        key={player.rank}
                                        className={`flex items-center justify-between p-4 rounded-lg border ${getRankBgColor(player.rank)} transition-all hover:scale-[1.01]`}
                                    >
                                        <div className="flex items-center gap-4">
                                            <div className="w-12 h-12 flex items-center justify-center font-bold text-lg">
                                                {player.rank <= 3 ? (
                                                    getRankBadge(player.rank)
                                                ) : (
                                                    <span className="text-slate-400">#{player.rank}</span>
                                                )}
                                            </div>
                                            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center text-white font-bold">
                                                {getInitials(player.name)}
                                            </div>
                                            <div>
                                                <p className="font-medium">{player.name}</p>
                                                <p className="text-sm text-slate-400">{player.username}</p>
                                            </div>
                                            <Badge variant="default" className="ml-2">
                                                <Star className="w-3 h-3 mr-1" />
                                                Lvl {player.level}
                                            </Badge>
                                        </div>
                                        <div className="flex items-center gap-6">
                                            <div className="text-right">
                                                <p className="text-sm text-slate-400">Wins</p>
                                                <p className="font-bold">{formatNumber(player.totalWins)}</p>
                                            </div>
                                            <div className="text-right">
                                                <p className="text-sm text-slate-400">Win Rate</p>
                                                <div className="flex items-center gap-2">
                                                    <TrendingUp className="w-4 h-4 text-emerald-400" />
                                                    <p className="font-bold text-emerald-400">{player.winRate.toFixed(1)}%</p>
                                                </div>
                                            </div>
                                            <div className="text-right min-w-[120px]">
                                                <p className="text-sm text-slate-400">Total Games</p>
                                                <p className="font-bold">{formatNumber(player.totalGames)}</p>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </Card>
                    </motion.div>
                </>
            )}
        </motion.div>
    );
}
